import React, { useEffect, useState } from "react";
import DoubleArrowIcon from "@/icons/withdraw/doubleArrowIcon";

import { Input } from "@/components/ui/popInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { networksByTokens, tokens } from "./dropdownData";
import { InputAmt } from "@/components/ui/inputamt";
import { useCurrencyExchange } from "@/services/currency.service";
import { walletDownIcon, walletUpIcon } from "@/constants";
import { useWithdraw } from "@/services/withdraw.service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useBalanceStore from "@/stores/user/balance.store";

interface WithdrawalFormProps {
  onWithdrawalSuccess: (usdAmount: string, usdtAmount: string) => void;
  onPendingWithdrawalExist: () => void;
}

const WithdrawalForm = ({
  onWithdrawalSuccess,
  onPendingWithdrawalExist,
}: WithdrawalFormProps) => {
  const { balance } = useBalanceStore();

  //set network based on tokens
  const [selectedTokenValue, setSelectedTokenValue] = useState(tokens[0].value);
  const [networkData, setNetworkData] = useState(
    networksByTokens[selectedTokenValue],
  );
  const [selectedNetworksValue, setSelectedNetworksValue] = useState(
    networkData[0].value,
  );
  const [address, setAddress] = useState("");
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);

  const useWithdrawMutation = useWithdraw();

  useEffect(() => {
    //update network when token changes
    setNetworkData(networksByTokens[selectedTokenValue]);
    setSelectedNetworksValue(networksByTokens[selectedTokenValue][0].value);
  }, [selectedTokenValue]);

  const [usdAmount, setUSDAmount] = useState("");
  const [otherTokenAmount, setOtherTokenAmount] = useState("");
  const [isExpand, setIsExapand] = useState(false);

  const currencyExchangeMutation = useCurrencyExchange();

  const handleCurrencySubmit = async (
    token1: any,
    token2: any,
    amount: any,
  ) => {
    const formData = {
      amount: parseInt(amount),
      token1: token1,
      token2: token2,
    };

    try {
      const response = await currencyExchangeMutation.mutateAsync(formData);
      return response.data.data.exchangeAmount;
    } catch (error) {
      console.error("Error occurred:", error);
    }
  };

  //hook for exchange rate
  const [exchangeRateForward, setExchangeRateForward] = useState(0);
  const [exchangeRateBackwards, setExchangeRateBackwards] = useState(0);

  //get the exchange rate when user selects tokens
  useEffect(() => {
    const fetchData = async () => {
      const tokenType =
        tokens.find((token) => token.value === selectedTokenValue)
          ?.currencyType ?? "";
      console.log(tokenType);

      //get the exchange rate
      const response = await handleCurrencySubmit("USDT", tokenType, 1);
      setExchangeRateForward(response.token1ToToken2ExchangeAmount);
      setExchangeRateBackwards(response.token2ToToken1ExchangeAmount);
    };
    fetchData();
  }, [selectedTokenValue]);

  //change the USD based on selected token amount
  const handleOtherTokenAmountChange = (value: any) => {
    if (value === "") {
      setUSDAmount("");
      setOtherTokenAmount("");
    } else {
      const otherAmount = parseFloat(value);
      console.log("Selected token to USD : ", exchangeRateBackwards);
      setUSDAmount((otherAmount * exchangeRateBackwards).toFixed(2));
      setOtherTokenAmount(value);
    }
  };

  //change the selected token amount based on the usd
  const handleUSDAmountChange = (value: any) => {
    if (value === "") {
      setUSDAmount("");
      setOtherTokenAmount("");
    } else {
      const usdValue = parseFloat(value);
      console.log("USD to selected token : ", exchangeRateForward);
      setUSDAmount(value);
      setOtherTokenAmount((usdValue * exchangeRateForward).toFixed(4));
    }
  };

  const handleWithdraw = async () => {
    if (parseInt(usdAmount) < 10) {
      return;
    }
    setIsWithdrawLoading(true);
    try {
      await useWithdrawMutation.mutateAsync({
        withdrawAmount: parseFloat(otherTokenAmount),
        withdrawUsdAmount: parseFloat(usdAmount),
        network: (() => {
          if (selectedNetworksValue === "ERC20") {
            return "eth";
          } else if (selectedNetworksValue === "BEP20") {
            return "bsc";
          } else if (selectedNetworksValue === "TRC20") {
            return "tron";
          }
          return "";
        })(),
        token:
          selectedTokenValue === "Ethereum" || selectedTokenValue === "Binance"
            ? "native"
            : "usdt",
        recipientAddress: address,
      });
      toast.success("Withdraw succeeded!");
      setIsWithdrawLoading(false);
      onWithdrawalSuccess(usdAmount, otherTokenAmount);
    } catch (error: any) {
      setIsWithdrawLoading(false);
      if (error.response.data.error.includes("pending")) {
        onPendingWithdrawalExist();
      }
      toast.error(error.response.data.error || "Withdraw failed!");
    }
  };

  return (
    <div className="mt-2">
      <h2 className="text-sm text-danger">
        All withdrawal requests are manually processed. Please contact our
        customer support via the live chat
      </h2>
      <div className="flex max-sm:justify-between">
        {/* Currency Dropdown */}
        <div className="mr-2 w-full max-md:mr-3">
          <span className="text-sm font-medium text-white/50">Currency</span>
          <div className="mt-1 w-full rounded-md border-1 border-solid border-white/5">
            <Select
              value={selectedTokenValue}
              onValueChange={setSelectedTokenValue}
            >
              <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                {tokens.map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className="w-full"
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex items-center text-wrap">
                        {item.Icon && <item.Icon size={18} />}
                        <div className="ml-2 max-w-32 truncate text-xs text-white/40">
                          {item.label}
                        </div>
                      </div>
                      <div className="flex text-white">{item.currencyType}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Network Dropdown */}
        <div className="w-full">
          <span className="text-sm font-medium text-white/50">Network</span>
          <div className="mt-1 w-full rounded-md border-1 border-solid border-white/0">
            <Select
              value={selectedNetworksValue}
              onValueChange={setSelectedNetworksValue}
            >
              <SelectTrigger className="w-full border-2 border-white/5 bg-white/5 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-2 border-white/5 bg-[#14142A]">
                {networkData.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <div className="flex items-center">
                      {item.Icon && <item.Icon size={20} />}
                      <span className="ml-2 text-white">{item.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-3 w-full">
        <div className="flex w-full justify-between">
          <label
            htmlFor="amount"
            className="mb-1 block text-sm font-medium text-white/50"
          >
            Amount
          </label>
          <div className="flex flex-row gap-3">
            <label
              htmlFor="amount"
              className="mb-1 block text-sm font-medium text-white/50"
            >
              Balance
            </label>
            <label
              htmlFor="amount"
              className="mb-1 block text-sm font-medium text-white"
            >
              $
              {balance?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </label>
          </div>
        </div>
        <div className="flex w-full gap-2">
          <div className="flex w-full items-center rounded-md border-2 border-solid border-white/10 bg-white/5 pl-1 text-white">
            <Input
              type="text"
              placeholder="$0.00"
              className="w-full border-none bg-transparent text-white selection:bg-none"
              value={usdAmount}
              onChange={(e) => handleUSDAmountChange(e.target.value)}
            />
          </div>
          <div className="flex w-10 items-center rounded-md border-1 p-2">
            <DoubleArrowIcon size={20} />
          </div>
          <div className="w-full">
            <InputAmt
              type="text"
              placeholder="0.00"
              className="w-full rounded-md border-2 border-solid border-white/10 bg-white/5 text-white"
              value={otherTokenAmount}
              beginAdornment={tokens.map((item) =>
                item.value === selectedTokenValue && item.Icon ? (
                  <div key={item.value}>{<item.Icon size={18} />}</div>
                ) : null,
              )}
              onChange={(e) => handleOtherTokenAmountChange(e.target.value)}
            />
          </div>
        </div>
        {parseInt(usdAmount) < 10 && (
          <p className="mt-2 text-xs text-danger">
            * Less than minimum withdrawal amount
          </p>
        )}
      </div>

      <div className="mb-3 mt-3 w-full">
        <label
          htmlFor="promo"
          className="mb-1 block text-sm font-medium text-white/50"
        >
          Address
        </label>
        <Input
          id="address"
          type="text"
          value={address}
          placeholder="Address"
          className="mt-1 w-full rounded-md border-2 border-solid border-white/10 bg-white/5 px-3 py-2 text-white"
          onChange={(e) => {
            setAddress(e.target.value);
          }}
        />
      </div>

      <div className="my-3 flex max-sm:flex-col sm:items-center sm:justify-between ">
        <div className="text-xs text-gray-300 text-white/50 ">
          Minimum withdrawal is $10 . Transaction fee is $0.00
        </div>
        <div className="flex items-center gap-2 self-center font-kanit max-sm:mt-2">
          <div className="text-sm font-medium text-[#2998FF]">
            Withdraw Limits
          </div>{" "}
          <button
            onClick={() => {
              setIsExapand(!isExpand);
            }}
          >
            {isExpand ? (
              <img src={walletUpIcon} alt="up" />
            ) : (
              <img src={walletDownIcon} alt="down" />
            )}
          </button>{" "}
        </div>
      </div>
      {isExpand && (
        <>
          <div className="mt-4 rounded-lg border border-[#1567C7] bg-[#8195FF]/10 p-3">
            <div className="mb-4 font-inter text-[15px] font-bold">
              Withdrawal Limits Based On KYC Status:
            </div>

            <div className="mb-4">
              <div className="font-inter text-[15px] font-bold">
                Level 1: Basic KYC
              </div>
              <ul className="list-disc pl-5 font-inter text-[15px] font-normal">
                <li>Withdrawal Limit: Maximum $5,000 per transaction</li>
                <li>Verification Requirements:</li>
                <ul className="list-disc pl-5">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Date of birth</li>
                  <li>Proof of address</li>
                  <li>Government-issued ID</li>
                  <li>Liveness check</li>
                </ul>
                <li>
                  Features: This level provides a straightforward verification
                  process, allowing players to enjoy moderate withdrawal limits
                  with essential security measures in place.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <div className="font-inter text-[15px] font-bold">
                Level 2: Advanced KYC
              </div>
              <ul className="list-disc pl-5 font-inter text-[15px] font-normal">
                <li>Withdrawal Limit: Maximum $5,000 per transaction</li>
                <li>Verification Requirements:</li>
                <ul className="list-disc pl-5">
                  <li>All requirements from Basic KYC</li>
                  <li>Proof of payment</li>
                </ul>
                <li>
                  Features: Players who complete Advanced KYC gain access to the
                  same transaction limits as Level 1 but receive added
                  credibility and compliance through proof of payment. Advanced
                  KYC is required once a player reaches $10,000 in total
                  withdrawals.
                </li>
                <li>Additional Withdrawal Rules:</li>
                <ul className="list-disc pl-5">
                  <li>Maximum Monthly Withdrawal Limit: $160,000</li>
                  <li>
                    Frequency of Withdrawals: Players may withdraw every 3 days,
                    with a maximum of $5,000 per transaction.
                  </li>
                </ul>
              </ul>
            </div>

            <div className="mb-4">
              <div className="font-inter text-[15px] font-bold">
                Level 3: Advanced (Enhanced KYC)
              </div>
              <ul className="list-disc pl-5 font-inter text-[15px] font-normal">
                <li>Withdrawal Limit: $10,000 per day</li>
                <li>
                  Requirements: Enhanced KYC including government-issued ID
                  verification, address proof, and phone number verification.
                </li>
                <li>
                  Features: Significantly higher withdrawal limits, encouraging
                  users to provide more detailed personal information for added
                  security and compliance.
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <div className="font-inter text-[15px] font-bold">
                Level 4: VIP (Full KYC and Additional Verification)
              </div>
              <ul className="list-disc pl-5 font-inter text-[15px] font-normal">
                <li>Withdrawal Limit: Unlimited per day</li>
                <li>
                  Requirements: Full KYC including social security number (or
                  equivalent), proof of income, and possibly biometric
                  verification.
                </li>
                <li>
                  Features: Maximum withdrawal limits, exclusive offers, and
                  personalized support, catering to high-stakes players and
                  ensuring maximum security and compliance.
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
      <Button
        className="mt-3 flex w-full rounded-md bg-button-gradient"
        onClick={handleWithdraw}
        disabled={isWithdrawLoading}
      >
        {isWithdrawLoading ? "Processing..." : "Request to Withdraw"}
      </Button>
    </div>
  );
};

export default WithdrawalForm;
